import React, { useMemo } from 'react';
import { InView } from 'react-intersection-observer';

import useAnchorLinkTracking from '@hooks/useAnchorLinkTracking';
import useEventTracking from '@hooks/useEventTracking';
import { highlight_path, topic_landing_path } from '@routes';
import { TrackingLocation, impression } from '@tracking';
import { capitalizeTopic, parameterize } from '@utils/formatting';

import ForYouReadingStat from './ReadingStat';
import ForYouSection from './Section';
import {
  EMPTY_RESULTS,
  FOR_YOU_ENGAGEMENT_LABELS,
  FOR_YOU_SECTION_IDS,
  TitleSizes,
} from './constants';
import type { ForYouReadingStatsProps } from './interfaces';

const READING_STAT_MIN_VALUE = 16;

const ForYouReadingStats = ({
  articlesRead,
  // minutesRead,
  mostReadTopic,
  mostReadNewsletters,
  newsletterAttrs,
}: ForYouReadingStatsProps) => {
  const { trackEvent } = useEventTracking();
  const readingStatsRef = useAnchorLinkTracking({
    trackingLocation: TrackingLocation.ReadingStat,
  });

  /**
   * Data transformation to get the most read newsletter
   * Data comes back in the form of ['newsletter_id: count', ...], so
   * we need to get the largest count and that newsletter's name
   */
  const mostReadNewsletter = useMemo(() => {
    return mostReadNewsletters?.reduce(
      (acc, pair) => {
        const [newsletterId, count] = pair.split(': ');

        if (acc.count < Number(count)) {
          const entry = newsletterAttrs?.[newsletterId];
          acc.count = Number(count);
          acc.newsletter = entry?.name || '';
          acc.slug = entry?.slug || '';
        }

        return acc;
      },
      { count: 0, newsletter: '', slug: '' }
    );
  }, [mostReadNewsletters, newsletterAttrs]);

  const mostReadNewsletterHref = mostReadNewsletter?.slug
    ? highlight_path(mostReadNewsletter.slug)
    : undefined;

  const capitalizedTopic = useMemo(() => capitalizeTopic(mostReadTopic || ''), [mostReadTopic]);

  const mostReadTopicHref = mostReadTopic
    ? topic_landing_path(parameterize(mostReadTopic))
    : undefined;

  const normalizedArticlesRead = useMemo(() => {
    if (!articlesRead) return 0;

    // Some people's data is wonky with semicolons like this: '16;10'
    const splitNumber = articlesRead.split(';');
    if (splitNumber.length === 1) return Number(splitNumber[0]);

    return Math.max(...splitNumber.map(Number));
  }, [articlesRead]);

  if (
    normalizedArticlesRead < READING_STAT_MIN_VALUE &&
    // !minutesRead &&
    !mostReadTopic &&
    !mostReadNewsletter?.newsletter
  ) {
    trackEvent(
      impression({ impressionElement: FOR_YOU_SECTION_IDS.ReadingStats, context: EMPTY_RESULTS })
    );
    return null;
  }

  return (
    <ForYouSection
      id={FOR_YOU_SECTION_IDS.ReadingStats}
      className="[&_+_nav]:mt-48"
      highlight={FOR_YOU_ENGAGEMENT_LABELS.Subscribed.ReadingStats.label}
      subtitle={FOR_YOU_ENGAGEMENT_LABELS.Subscribed.ReadingStats.subtitle}
      title={
        <>
          <span className="block sm:hidden">
            {FOR_YOU_ENGAGEMENT_LABELS.Subscribed.ReadingStats.mobileTitle}
          </span>
          <span className="hidden sm:block">
            {FOR_YOU_ENGAGEMENT_LABELS.Subscribed.ReadingStats.title}
          </span>
        </>
      }
      titleSize={TitleSizes.Large}
      disableBorder
    >
      <InView
        onChange={(inView) => {
          inView && trackEvent(impression({ impressionElement: FOR_YOU_SECTION_IDS.ReadingStats }));
        }}
        triggerOnce
      >
        <div
          ref={readingStatsRef}
          className="relative mt-48 flex flex-row items-stretch justify-center gap-0 sm:gap-32 lg:gap-64"
        >
          {normalizedArticlesRead >= READING_STAT_MIN_VALUE && (
            <ForYouReadingStat
              index={0}
              isLast={false}
              name="Articles Read"
              value={normalizedArticlesRead}
            />
          )}
          {/**
           * TODO: Waiting for data to be normalized before displaying. No ticket yet.
           * {minutesRead && (
            <ForYouReadingStat index={1} name="Time Spent" value={`${minutesRead} mins`} />
          )} */}
          {mostReadTopic && (
            <ForYouReadingStat
              index={2}
              isLast={false}
              name="Top Topic"
              value={capitalizedTopic}
              href={mostReadTopicHref}
              small
            />
          )}
          {mostReadNewsletter?.newsletter && (
            <ForYouReadingStat
              index={3}
              name={
                <span>
                  <span className="hidden sm:inline">Most-Read</span>
                  <span className="inline sm:hidden">Top</span> Newsletter
                </span>
              }
              value={mostReadNewsletter.newsletter}
              href={mostReadNewsletterHref}
              isLast
              small
            />
          )}
        </div>
      </InView>
    </ForYouSection>
  );
};

export default ForYouReadingStats;
